import React, { useEffect, useState, useMemo } from 'react'
import { toggleSidebar } from '../../../redux/components/components-slice'
import { useDispatch, useSelector } from 'react-redux'
import DashboardLayout from '../../../components/layouts/dashboard'
import FormSelect from '../../../components/form-select'
import EmployeeSVG from '../../../assets/svg/employee-new.svg'
import PayoutSVG from '../../../assets/svg/last-payout-new.svg'
import WalletSVG from '../../../assets/svg/wallet-balance-new.svg'
import PayrollOverviewChart from '../../../components/payroll-section/dashboard/payroll-overview-chart'
import PayrollChart from '../../../components/payroll-section/dashboard/payroll-chart'
import { Card } from '../../../components/card'
import {
  useGetDashboard,
  useGetMoneyInflow,
} from '../../../redux/dashboard/hook'
import LoadingBar from '../../../components/loader-bar'
import { useTour } from '@reactour/tour'
import {
  formatNumber,
  getLast12MonthsDates,
  getLast12MonthLabels,
} from '../../../utils/functions'
import DemoVideoModal from '../../../components/modal/demo-videos'
import { useGetRecentPayroll } from '../../../redux/payroll/hook'
import DataTable from '../../../components/datatable'
import {
  PAGINATION_DEFAULT,
  advancedPayrollStatusConfig,
} from '../../../utils/constants'

const PayrollStatChart = React.memo(({ moneyInflow }) => {
  const payrollStat = useMemo(() => {
    const months = getLast12MonthLabels()
    return {
      data: {
        labels: months,
        datasets: [
          {
            label: 'Money In',
            data: moneyInflow?.moneyIn || Array(12).fill(0),
            backgroundColor: '#505F9F',
          },
          {
            label: 'Money Out',
            data: moneyInflow?.moneyOut || Array(12).fill(0),
            backgroundColor: '#A1A6BB',
          },
        ],
      },
    }
  }, [moneyInflow])

  return <PayrollChart data={payrollStat} />
})

const Dashboard = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.userSlice)
  const { data: dashboard, isLoading } = useGetDashboard()

  const [pagination, setPagination] = useState({
    limit: PAGINATION_DEFAULT.limit,
    page: PAGINATION_DEFAULT.page,
    statusFilter: PAGINATION_DEFAULT.statusFilter,
    search: '',
  })

  const { data: recentPayroll } = useGetRecentPayroll(
    pagination.limit,
    pagination.page,
    pagination.statusFilter,
    pagination.search
  )

  const { startDate, endDate } = getLast12MonthsDates()
  const { data: moneyInflow } = useGetMoneyInflow(startDate, endDate)

  useEffect(() => {
    if (window.innerWidth < 851) dispatch(toggleSidebar())
  }, [])

  const [showOnboardingVideo, setShowOnboardingVideo] = useState(false)
  const [sortOption, setSortOption] = useState('Yearly')
  const sortOptions = [{ value: 'Yearly', label: 'Yearly' }]

  const { setIsOpen } = useTour()

  const dashboardCards = useMemo(
    () => [
      {
        title: 'EMPLOYEES',
        value: dashboard?.activeEmployeeCount,
        icon: EmployeeSVG,
      },
      {
        title: 'WALLET BALANCE',
        value: `NGN ${
          dashboard?.walletBalance
            ? formatNumber(dashboard.walletBalance, 2)
            : 0
        }`,
        icon: WalletSVG,
      },
      {
        title: 'LAST PAYOUT',
        value: dashboard?.lastPayrollPayout
          ? formatNumber(dashboard.lastPayrollPayout, 2)
          : 0,
        icon: PayoutSVG,
      },
    ],
    [dashboard]
  )

  const donutData = useMemo(() => {
    if (!dashboard) return null
    const {
      compensation = 0,
      deductions = 0,
      other_deductions = 0,
      reimbursements = 0,
    } = dashboard.lastSixMonthBreakdown ?? {}

    return {
      data: {
        labels: [
          'Compensation',
          'Statutory Deduction',
          'Other Deductions',
          'Reimbursibles',
        ],
        options: { plugins: { legend: { display: false } } },
        datasets: [
          {
            cutout: '70%',
            label: '# of Votes',
            data: [compensation, deductions, other_deductions, reimbursements],
            backgroundColor: [
              '#3943B7',
              '#f79421',
              'rgb(178,34,34)',
              '#0d5225',
            ],
          },
        ],
      },
      legends: [
        { title: 'Compensation', color: 'bg-secondary-2' },
        { title: 'Statutory Deduction', color: 'bg-[#f79421]' },
        { title: 'Other Deduction', color: 'bg-[rgb(178,34,34)]' },
        { title: 'Reimbursements', color: 'bg-[#0d5225]' },
      ],
      total: formatNumber(
        parseInt(compensation) +
          parseInt(deductions) +
          parseInt(other_deductions) +
          parseInt(reimbursements),
        2
      ),
    }
  }, [dashboard])


  const tableConfig = {
    headers: [
      'Employee Name',
      'Bank Info',
      'Created At',
      'Payroll Date',
      'Total Amount',
      'Amount Paid',
      'Status',
    ],
    keys: [
      'employee_name',
      'bank_name',
      'created',
      'formatted_month',
      'formatted_total_amount',
      'formatted_amount_paid',
      'status',
    ],
    mobileHeader: {
      left: { title: 'Employee Name', key: 'employee_name' },
      right: { title: 'Total Amount', key: 'formatted_total_amount' },
    },
  }

  return (
    <DashboardLayout title='Payroll Dashboard'>
      {showOnboardingVideo && (
        <DemoVideoModal
          isOpen
          closeModal={() => setShowOnboardingVideo(false)}
        />
      )}
      <div className='flex flex-col h-full -mt-2'>
        <div className='flex flex-col md:flex-row justify-between items-center gap-3'>
          <div className='flex flex-col items-start justify-start'>
            <span className='header-3'>Welcome, {user?.first_name}.</span>
            <span className='text-[13px] text-gray-500'>
              Quick Guide? &nbsp;
              <span
                onClick={() => setIsOpen(true)}
                className='text-[13px] mt-2 text-secondary-2 font-bold hover:font-extrabold duration-300 cursor-pointer hover:scale-105 hover:underline'
              >
                Click Here
              </span>
            </span>
          </div>
        </div>
        <>
          <div
            id='dashboard-overview'
            className='mt-4 grid md:grid-cols-2 lg:grid-cols-3 grid-cols-1 gap-4 w-full'
          >
            {dashboardCards.map((item, index) => (
              <Card
                key={index}
                title={item.title}
                subtitle={item.value}
                icon={item.icon}
                bgColor='transparent'
              />
            ))}
          </div>

          <div className='flex flex-col w-full lg:flex-row gap-3 justify-between mt-4 rounded-md'>
            <div className='flex flex-col w-full lg:flex-row gap-3 justify-between mt-4 rounded-md'>
              <div className='w-[100%] lg:w-[67%] h-[450px] border rounded-md flex flex-col'>
                <div className='flex w-full flex-row justify-between items-center p-3'>
                  <span className='p2'>Performance Statistics</span>
                  <div className='hidden md:flex flex-row items-center gap-2'>
                    <div className='bg-secondary-2 w-[10px] h-[10px] rounded-[10px] pr-2'></div>
                    <span>Payroll Management.</span>
                  </div>
                  {/* <div className='flex items-center h-[27px]'>
                    <FormSelect
                      defaultValue={sortOption}
                      options={sortOptions}
                      onChange={setSortOption}
                      type='default'
                      extraClass='h-[27px] w-[140px] !text-[12px]'
                    />
                  </div> */}
                </div>
                <hr className='divider -mt-2' />
                <div className='h-full w-[100%] md:px-1 px-2'>
                  <PayrollStatChart moneyInflow={moneyInflow} />
                </div>
              </div>

              <div className='w-[100%] h-[450px] lg:w-[33%] flex flex-col'>
                <div className='flex flex-col h-full bg-color-white px-4 rounded-md border'>
                  <div className='flex py-2 justify-between items-center'>
                    <span className='text-[14px] font-extrabold'>
                      Payroll Overview
                    </span>
                    {/* <FormSelect
                      defaultValue={sortOption}
                      options={sortOptions}
                      onChange={setSortOption}
                      type='default'
                      extraClass='h-[27px] w-[140px] !text-[12px]'
                    /> */}
                  </div>
                  <hr className='divider mt-2 mb-2' />
                  <div className='flex h-[450px]'>
                    {donutData && (
                      <div className='w-[100%]'>
                        <PayrollOverviewChart data={donutData} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-2.5 border rounded-md p-3 mt-4'>
            <h1 className='font-medium text-[18px]'>Recent Payroll</h1>
            <DataTable
              data={recentPayroll}
              tableConfig={tableConfig}
              noCheck={true}
              statusConfig={advancedPayrollStatusConfig}
              updatePagination={(data) => setPagination(data)}
              filterByStatus={true}
              searchData={true}
            />
          </div>
        </>
      </div>
    </DashboardLayout>
  )
}

export default Dashboard
